/**
 * Dashboard filters (2026-10-25): a date range and any number of campaigns.
 *
 * State lives in the URL (?range=30d&from=&to=&campaigns=a,b) so a filtered
 * view survives a refresh and can be shared. Dates are the viewer's local
 * days; the API gets ISO instants (start of the first day, start of the
 * day after the last) plus tz_offset, see @dealcloser/db/stats-scope.
 */

export type RangePreset = 'all' | 'today' | '7d' | '30d' | '90d' | 'this_month' | 'last_month' | 'custom'

export const RANGE_PRESETS: readonly { readonly value: RangePreset; readonly label: string }[] = [
  { value: 'all', label: 'All time' },
  { value: 'today', label: 'Today' },
  { value: '7d', label: 'Last 7 days' },
  { value: '30d', label: 'Last 30 days' },
  { value: '90d', label: 'Last 90 days' },
  { value: 'this_month', label: 'This month' },
  { value: 'last_month', label: 'Last month' },
  { value: 'custom', label: 'Custom range' },
]

export type DashboardFilters = {
  readonly range: RangePreset
  /** 'YYYY-MM-DD', custom range only. */
  readonly from: string
  /** 'YYYY-MM-DD' inclusive, custom range only. */
  readonly to: string
  readonly campaignIds: readonly string[]
}

export const DEFAULT_FILTERS: DashboardFilters = { range: 'all', from: '', to: '', campaignIds: [] }

const DAY = /^\d{4}-\d{2}-\d{2}$/
const isPreset = (v: string | null): v is RangePreset => RANGE_PRESETS.some((p) => p.value === v)

/** Local midnight of a 'YYYY-MM-DD' day. */
function localMidnight(day: string): Date {
  const [y, m, d] = day.split('-').map(Number)
  return new Date(y!, m! - 1, d!)
}

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate())
const addDays = (d: Date, n: number) => new Date(d.getFullYear(), d.getMonth(), d.getDate() + n)

export function readFilters(params: URLSearchParams): DashboardFilters {
  const range = params.get('range')
  const from = params.get('from') ?? ''
  const to = params.get('to') ?? ''
  const campaigns = (params.get('campaigns') ?? '').split(',').map((s) => s.trim()).filter(Boolean)
  return {
    range: isPreset(range) ? range : 'all',
    from: DAY.test(from) ? from : '',
    to: DAY.test(to) ? to : '',
    campaignIds: [...new Set(campaigns)],
  }
}

/** Write the filters onto a copy of `params`, dropping defaults. */
export function writeFilters(params: URLSearchParams, f: DashboardFilters): URLSearchParams {
  const next = new URLSearchParams(params)
  for (const k of ['range', 'from', 'to', 'campaigns']) next.delete(k)
  if (f.range !== 'all') next.set('range', f.range)
  if (f.range === 'custom') {
    if (f.from) next.set('from', f.from)
    if (f.to) next.set('to', f.to)
  }
  if (f.campaignIds.length) next.set('campaigns', f.campaignIds.join(','))
  return next
}

/** [from, to) instants of the range; null ends are open. */
export function resolveRange(f: DashboardFilters, now: Date = new Date()): { from: Date | null; to: Date | null } {
  const today = startOfDay(now)
  const tomorrow = addDays(today, 1)
  switch (f.range) {
    case 'all':
      return { from: null, to: null }
    case 'today':
      return { from: today, to: tomorrow }
    case '7d':
      return { from: addDays(today, -6), to: tomorrow }
    case '30d':
      return { from: addDays(today, -29), to: tomorrow }
    case '90d':
      return { from: addDays(today, -89), to: tomorrow }
    case 'this_month':
      return { from: new Date(now.getFullYear(), now.getMonth(), 1), to: tomorrow }
    case 'last_month':
      return { from: new Date(now.getFullYear(), now.getMonth() - 1, 1), to: new Date(now.getFullYear(), now.getMonth(), 1) }
    case 'custom': {
      let from = f.from ? localMidnight(f.from) : null
      let to = f.to ? addDays(localMidnight(f.to), 1) : null
      // Picked backwards: swap rather than show an error.
      if (from && to && from >= to) [from, to] = [addDays(to, -1), addDays(from, 1)]
      return { from, to }
    }
  }
}

/** The same-length window just before the range, for the cards' trend. */
export function previousRange(range: { from: Date | null; to: Date | null }): { from: Date; to: Date } | null {
  if (!range.from || !range.to) return null
  const len = range.to.getTime() - range.from.getTime()
  return { from: new Date(range.from.getTime() - len), to: range.from }
}

/** Query string for the stats endpoints ('' when nothing is filtered). */
export function statsQuery(range: { from: Date | null; to: Date | null }, campaignIds: readonly string[]): string {
  const p = new URLSearchParams()
  if (range.from) p.set('from', range.from.toISOString())
  if (range.to) p.set('to', range.to.toISOString())
  if (campaignIds.length) p.set('campaign_ids', [...campaignIds].sort().join(','))
  if (range.from || range.to) p.set('tz_offset', String((range.from ?? range.to)!.getTimezoneOffset()))
  const s = p.toString()
  return s ? `?${s}` : ''
}

export function isFiltered(f: DashboardFilters): boolean {
  return f.range !== 'all' || f.campaignIds.length > 0
}

const fmtDay = (d: Date) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })

/** "Oct 1, 2026 - Oct 7, 2026" for a resolved range, or the preset's label. */
export function rangeLabel(f: DashboardFilters, now: Date = new Date()): string {
  if (f.range !== 'custom') return RANGE_PRESETS.find((p) => p.value === f.range)?.label ?? 'All time'
  const { from, to } = resolveRange(f, now)
  if (from && to) {
    const last = addDays(to, -1)
    return from.getTime() === last.getTime() ? fmtDay(from) : `${fmtDay(from)} to ${fmtDay(last)}`
  }
  if (from) return `Since ${fmtDay(from)}`
  if (to) return `Until ${fmtDay(addDays(to, -1))}`
  return 'All time'
}

/** "previous 7 days" for the trend line. */
export function previousLabel(range: { from: Date | null; to: Date | null }): string {
  if (!range.from || !range.to) return ''
  const days = Math.round((range.to.getTime() - range.from.getTime()) / 86_400_000)
  return days === 1 ? 'the previous day' : `the previous ${days} days`
}
